import type { OperatorDeskWorkQuestion } from './operatorDeskWork';

/**
 * Operator Desk water delivery journey — structured timeline parity with haul/welding.
 * Water has no separate geofence arrival table yet; delivered_at is the on-site clock.
 */

export type WaterDeliveryJourney = {
  id: string;
  workId: string;
  title: string;
  status: string;
  statusLabel: string;
  contractorName: string | null;
  driverName: string | null;
  waterTypeLabel: string | null;
  createdAt: string | null;
  claimedAt: string | null;
  enRouteAt: string | null;
  /** On-site / delivery proof clock (Attention arrival). */
  arrivedAt: string | null;
  deliveredAt: string | null;
  cancelledAt: string | null;
  needBy: string | null;
  /** Clock form of need_by for Attention schedule kinds. */
  scheduledAt: string | null;
  proofPhotos: Array<{ id: string; url: string; caption: string; at: string | null }>;
  questions: OperatorDeskWorkQuestion[];
};

/** Date-only need_by/delivery_date → end-of-day UTC clock so Attention can score it. */
export function serviceArrivalScheduleClock(raw: unknown): string | null {
  const value = String(raw || '').trim();
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T23:59:59Z`;
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?/.test(value)) {
    if (value.includes('T')) return /Z$|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`;
    const normalized = value.replace(' ', 'T');
    return /Z$|[+-]\d{2}:?\d{2}$/.test(normalized) ? normalized : `${normalized}Z`;
  }
  return null;
}

export function waterJourneyLabel(
  job: Pick<WaterDeliveryJourney, 'status' | 'claimedAt' | 'enRouteAt' | 'deliveredAt' | 'cancelledAt'>,
): string {
  const status = String(job.status || '').toLowerCase();
  if (status === 'cancelled' || status === 'canceled' || job.cancelledAt) return 'Cancelled';
  if (status === 'delivered' || job.deliveredAt) return 'Delivered';
  if (status === 'en_route' || job.enRouteAt) return 'En route';
  if (status === 'accepted' || status === 'claimed' || job.claimedAt) return 'Accepted';
  if (status === 'open' || status === 'pending_pay' || status === 'checkout_pending' || status === 'paid') return 'Requested';
  return 'Requested';
}

export function waterJourneySteps(job: WaterDeliveryJourney) {
  const rows = [
    { id: 'requested', label: 'Requested', at: job.createdAt },
    { id: 'accepted', label: 'Accepted', at: job.claimedAt },
    { id: 'en_route', label: 'En route', at: job.enRouteAt },
    { id: 'delivered', label: 'Delivered', at: job.deliveredAt },
  ];
  const next = rows.findIndex((row) => !row.at);
  const terminal = Boolean(job.cancelledAt) || job.status === 'cancelled' || job.status === 'canceled';
  return rows.map((row, index) => ({
    ...row,
    state: (row.at
      ? 'complete'
      : index === next && !terminal
        ? 'active'
        : 'locked') as 'complete' | 'active' | 'locked',
  }));
}

export function buildWaterDeliveryJourney(row: Record<string, unknown>, extras?: {
  proofPhotos?: WaterDeliveryJourney['proofPhotos'];
  questions?: OperatorDeskWorkQuestion[];
}): WaterDeliveryJourney {
  const id = String(row.id);
  const status = String(row.status || 'open');
  const deliveredAt = row.delivered_at != null ? String(row.delivered_at) : null;
  const needBy = row.need_by != null ? String(row.need_by) : null;
  const journey: WaterDeliveryJourney = {
    id,
    workId: `water:${id}`,
    title: String(row.delivery_facility_name || row.delivery_api_number || row.water_type_label || 'Water'),
    status,
    statusLabel: '',
    contractorName: row.provider_company != null ? String(row.provider_company) : null,
    driverName: row.driver_username != null ? String(row.driver_username) : null,
    waterTypeLabel: row.water_type_label != null ? String(row.water_type_label) : null,
    createdAt: row.created_at != null ? String(row.created_at) : null,
    claimedAt: row.claimed_at != null ? String(row.claimed_at) : null,
    enRouteAt: row.en_route_at != null ? String(row.en_route_at) : null,
    arrivedAt: deliveredAt,
    deliveredAt,
    cancelledAt: row.cancelled_at != null ? String(row.cancelled_at) : null,
    needBy,
    scheduledAt: serviceArrivalScheduleClock(needBy),
    proofPhotos: extras?.proofPhotos || [],
    questions: extras?.questions || [],
  };
  journey.statusLabel = waterJourneyLabel(journey);
  return journey;
}
